import ColorLens from "@mui/icons-material/ColorLens";
import Image from "@mui/icons-material/Image";
import Tag from "@mui/icons-material/Tag";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import axios from "axios";
import React, { Suspense, lazy, useContext, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import MainContext from "./Components/Context/Context";
import Header from "./Components/Header/Header";
import ErrorBoundary from "./ErrorBoundary";

import UsernameGenerator from "./UsernameGenerator";
import CaptionGenerator from "./CaptionGenerator";
import BioGenerator from "./BioGenerator";
import NewHashTagGenerator from "./NewHashtagGenerator";

const FreeToolsLeftDrawer = lazy(
  () => import("./Components/FreeToolsLeftDrawer/FreeToolsLeftDrawer")
);
const Footer = lazy(() => import("./Components/Footer"));

const AboutUs = lazy(() => import("./Pages/AboutUs"));
const BackgroundChanger = lazy(() => import("./Pages/BackgroundChanger"));
const BackgroundRemover = lazy(() => import("./Pages/BackgroundRemover"));
const FreeHashtagGenerator = lazy(() => import("./Pages/FreeHashtagGenerator"));
const MemeGenerator = lazy(() => import("./Pages/MemeGenerator"));
const InstagramPostMaker = lazy(() => import("./Pages/InstagramPostMaker"));
const HomePage = lazy(() => import("./Pages/HomePage"));
const PaletteGenerator = lazy(() => import("./Pages/PaletteGenerator"));
const PhotoPicker = lazy(() => import("./Pages/PhotoPicker"));
const StockImage = lazy(() => import("./Pages/StockImage"));
const VideoThumbnail = lazy(() => import("./Pages/VideoThumbnail"));
const WelcomePage = lazy(() => import("./Pages/WelcomePage"));
const BackgroundChangerSEO = lazy(() => import("./SEOPages/BackgroundChangerSEO"));
const BackgroundRemoverSEO = lazy(() => import("./SEOPages/BackgroundRemoverSEO"));
const FreeHashtagGeneratorSEO = lazy(() => import("./SEOPages/HashtagGeneratorSEO"));
const InstagramPostMakerSEO = lazy(() => import("./SEOPages/InstagramPostMakerSEO"));
const InstagramReelMakerSEO = lazy(() => import("./SEOPages/InstagramReelMakerSEO"));
const FacebookAdMakerSEO = lazy(() => import("./SEOPages/FacebookAdMakerSEO"));
const WhatsAppStatusMakerSEO = lazy(() => import("./SEOPages/WhatsAppStatusMakerSEO"));
const LinkedinCarouselMakerSEO = lazy(() => import("./SEOPages/LinkedinCarouselMakerSEO"));
const GoogleAdSEO = lazy(() => import("./SEOPages/GoogleAdSEO"));
const MobileAdSEO = lazy(() => import("./SEOPages/MobileAdSEO"));
const TwitterAdMakerSEO = lazy(() => import("./SEOPages/TwitterAdMakerSEO"));
const FacebookPostMakerSEO = lazy(() => import("./SEOPages/FacebookPostMakerSEO"));
const LinkedinAdMakerSEO = lazy(() => import("./SEOPages/LinkedinAdMakerSEO"));
const TwitterCarouselMakerSEO = lazy(() => import("./SEOPages/TwitterCarouselMakerSEO"));
const InstagramStoryMakerSEO = lazy(() => import("./SEOPages/InstagramStoryMakerSEO"));
const InstagramAdMakerSEO = lazy(() => import("./SEOPages/InstagramAdMakerSEO"));
const SocialMediaPostGeneratorSEO = lazy(() => import("./SEOPages/SocialMediaPostGeneratorSEO"));
const PaletteGeneratorSEO = lazy(() => import("./SEOPages/PaletteGeneratorSEO"));
const ShortsMakerSEO = lazy(() => import("./SEOPages/ShortsMakerSEO"));
const StockImageSEO = lazy(() => import("./SEOPages/StockImageSEO"));
const TikTokMakerSEO = lazy(() => import("./SEOPages/TikTokMakerSEO"));
const EcommercePostMakerSEO = lazy(() => import("./SEOPages/EcommercePostMakerSEO"));
const EcommerceVideoMakerSEO = lazy(() => import("./SEOPages/EcommerceVideoMakerSEO"));
const SocialMediaAdCopySEO = lazy(() => import("./SEOPages/SocialMediaAdCopySEO"));
const BlogToPostMakerSEO = lazy(() => import("./SEOPages/BlogToPostMakerSEO"));
const TweetToImageGenerater = lazy(() => import("./Pages/TweetToImageGenerater"));
const InstagramFeedMaker = lazy(() => import("./Pages/InstagramFeedMaker"));
const YouTubeTitleGenerater = lazy(() => import("./Pages/YouTubeTitleGenerater"));
const ProductDescriptionGenerator = lazy(() => import("./Pages/ProductDescriptionGenerator"));
const PodcastTitleGenerator = lazy(() => import("./Pages/PodcastTitleGenerator"));
const BlogTitleGenerator = lazy(() => import("./Pages/BlogTitleGenerator"));
const InstagramThreadsPostGenerator = lazy(() => import("./Pages/InstagramThreadsPostGenerator"));
const InstagramNotesGenerator = lazy(() => import("./Pages/InstagramNotesGenerator"));
const LinkedinPostGenerator = lazy(() => import("./Pages/LinkedinPostGenerator"));
const QuotesGenerator = lazy(() => import("./Pages/QuotesGenerator"));
const TweetGenerator = lazy(() => import("./Pages/TweetGenerator"));

const YouTubeChannelDescriptionGenerator = lazy(
  () => import("./Pages/YouTubeChannelDescriptionGenerator")
);
const YouTubeVideoDescriptionGenerator = lazy(
  () => import("./Pages/YouTubeVideoDescriptionGenerator")
);

//Ecommerce
const EcommerceBrandNameGenerator = lazy(() => import("./Pages/EcommerceBrandNameGenerator"));
const BrandSloganGenerator = lazy(() => import("./Pages/BrandSloganGenerator"));
const CallToActionGenerator = lazy(() => import("./Pages/CallToActionGenerator"));

function Base() {
  const { isNotWebview } = useContext(MainContext);
  return (
    <>
      <Box sx={{ pb: !isNotWebview ? "2rem" : "0" }}>
        <BrowserRouter>
          <RouteItems />
        </BrowserRouter>
      </Box>
    </>
  );
}

const NavigationWrapper = (props) => {
  const location = useLocation();
  return (
    <>
      {!location.pathname.endsWith("/") && <Navigate to={"./"} />}
      {props.children}
    </>
  );
};

const HeaderFooterWrapper = (props) => {
  const { isNotWebview } = useContext(MainContext);
  return (
    <>
      {isNotWebview && <Header />}
      {props.children}
      {isNotWebview && <Footer />}
    </>
  );
};

const RouteItems = () => {
  const { isNotWebview } = useContext(MainContext);
  // const [value, setValue] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const locationName = useLocation();
  const navigate = useNavigate();
  // Set Axios Defaults
  axios.defaults.baseURL = "https://brain.predis.ai/tools";

  useEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [locationName]);

  const handleClick = (pathname) => {
    navigate(pathname);
  };

  const handleListItemClick = (event, index) => {
    setSelectedIndex(index);
  };

  return (
    <>
      <div>
        {/* Assuming isNotWebview and locationName.pathname are used to conditionally render the FreeToolsLeftDrawer */}
        {!isNotWebview && locationName.pathname !== "/free-ai-tools/welcome" && (
          <Suspense fallback={<div>Loading...</div>}>
            <FreeToolsLeftDrawer
              handleListItemClick={handleListItemClick}
              selectedIndex={selectedIndex}
            />
          </Suspense>
        )}
      </div>
      {!isNotWebview &&
        locationName.pathname !== "/free-ai-tools/welcome" &&
        locationName.pathname !== "/free-ai-tools/about-us" && (
          <Paper
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 2
            }}
            elevation={3}
          >
            <BottomNavigation
              showLabels
              value={selectedIndex}
              onChange={(event, newValue) => {
                setSelectedIndex(newValue);
              }}
            >
              <BottomNavigationAction
                label='Hashtag'
                icon={<Tag />}
                sx={{ p: 1 }}
                onClick={() => handleClick("free-ai-tools/free-hashtag-generator")}
              />
              <BottomNavigationAction
                label='Color'
                icon={<ColorLens />}
                sx={{ p: 1 }}
                onClick={() => handleClick("free-ai-tools/color-palette-generator")}
              />
              <BottomNavigationAction
                label='Photo'
                icon={<Image />}
                sx={{ p: 1 }}
                onClick={() => handleClick("free-ai-tools/stock-image-search")}
              />
              <BottomNavigationAction
                label='Background'
                icon={<WallpaperIcon />}
                sx={{ p: 1 }}
                onClick={() => handleClick("free-ai-tools/background-remover")}
              />
            </BottomNavigation>
          </Paper>
        )}
      <Suspense
        fallback={
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center"
            }}
          >
            Loading...
            <Box sx={{ width: "300px" }}>
              <LinearProgress />
            </Box>
          </Box>
        }
      >
        <Routes>
          <>
            {/* free-ai-tools */}

            <Route
              path='free-ai-tools/'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                      <HeaderFooterWrapper>
                        <HomePage />
                      </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              path='free-ai-tools/welcome/'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <WelcomePage />
                    </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              path='free-ai-tools/about-us/'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <AboutUs />
                    </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              path='free-ai-tools/ai-meme-generator/'
              exact
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <MemeGenerator />
                    </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              path='free-ai-tools/ai-meme-generator/:memeParam'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <MemeGenerator />
                    </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              path='free-ai-tools/free-hashtag-generator/'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <FreeHashtagGenerator />
                    </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              exact
              path='free-ai-tools/free-hashtag-generator/:slug'
              element={
                <ErrorBoundary>
                  <NavigationWrapper>
                    <HeaderFooterWrapper>
                      <FreeHashtagGeneratorSEO />
                    </HeaderFooterWrapper>
                  </NavigationWrapper>
                </ErrorBoundary>
              }
            />
            <Route
              path='free-ai-tools/color-palette-generator/'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                        <HeaderFooterWrapper>
                            <PaletteGenerator />
                        </HeaderFooterWrapper>
                    </NavigationWrapper>                    
                  </ErrorBoundary>
                </>
              }
            />
            <Route
              exact
              path='free-ai-tools/color-palette-generator/:slug'
              element={
                <>
                  <ErrorBoundary>
                    <NavigationWrapper>
                      <HeaderFooterWrapper>
                        <PaletteGeneratorSEO />
                      </HeaderFooterWrapper>
                    </NavigationWrapper>
                  </ErrorBoundary>
                </>
              }
            />
          </>
          <Route
            path='free-ai-tools/best-photo-picker/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <PhotoPicker />
                  </HeaderFooterWrapper>
                </NavigationWrapper>                
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/background-remover/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                  <BackgroundRemover />
                </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/instagram-feed-maker/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramFeedMaker />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/background-remover/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BackgroundRemoverSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/video-thumbnail-picker/'
            element={
              <ErrorBoundary>
                  <NavigationWrapper>
                      <HeaderFooterWrapper>
                          <VideoThumbnail />
                    </HeaderFooterWrapper>
                  </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/background-color-changer/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                   <HeaderFooterWrapper>
                      <BackgroundChanger />
                   </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/tweet-to-image-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <TweetToImageGenerater />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/youtube-title-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <YouTubeTitleGenerater />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/product-description-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <ProductDescriptionGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/ecommerce-brand-name-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <EcommerceBrandNameGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/ecommerce-brand-slogan-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BrandSloganGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/tweet-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <TweetGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            exact
            path='free-ai-tools/background-color-changer/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BackgroundChangerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/stock-image-search/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <StockImage />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/stock-image-search/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <StockImageSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/instagram-post-maker/'
            element={
              <ErrorBoundary>
                <HeaderFooterWrapper>
                  <InstagramPostMaker />
                </HeaderFooterWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/instagram-post-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramPostMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/instagram-ad-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramAdMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/instagram-reel-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramReelMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/facebook-ad-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <FacebookAdMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/whatsapp-status-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <WhatsAppStatusMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/linkedin-carousel-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <LinkedinCarouselMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/google-display-ads-generator/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <GoogleAdSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/mobile-ad-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <MobileAdSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/twitter-ad-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <TwitterAdMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/twitter-carousel-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <TwitterCarouselMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/linkedin-ad-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <LinkedinAdMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/facebook-post-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <FacebookPostMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/instagram-story-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramStoryMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/social-media-post-generator/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <SocialMediaPostGeneratorSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/ecommerce-social-media-post-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <EcommercePostMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/ecommerce-product-video-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <EcommerceVideoMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/social-media-ad-copy-generator/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <SocialMediaAdCopySEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/blog-to-post-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BlogToPostMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/tiktok-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <TikTokMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            exact
            path='free-ai-tools/youtube-shorts-maker/:slug'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <ShortsMakerSEO />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/:slug-username-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <UsernameGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/:slug-caption-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <CaptionGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/:slug-bio-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BioGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/podcast-title-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <PodcastTitleGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/blog-title-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <BlogTitleGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/instagram-threads-post-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramThreadsPostGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/instagram-notes-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <InstagramNotesGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/linkedin-post-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <LinkedinPostGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/quotes-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <QuotesGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/youtube-channel-description-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <YouTubeChannelDescriptionGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />

          <Route
            path='free-ai-tools/youtube-video-description-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <YouTubeVideoDescriptionGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/call-to-action-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <CallToActionGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='free-ai-tools/:slug-hashtag-generator/'
            element={
              <ErrorBoundary>
                <NavigationWrapper>
                  <HeaderFooterWrapper>
                    <NewHashTagGenerator />
                  </HeaderFooterWrapper>
                </NavigationWrapper>
              </ErrorBoundary>
            }
          />
          <Route
            path='*'
            element={
              <HeaderFooterWrapper>
                <main style={{ padding: "1rem" }}>
                  <p>There's nothing here!</p>
                </main>
              </HeaderFooterWrapper>
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};

export default Base;
